import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Form, Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ADDON_LIST_IND,
  CUSTOMER_USAGE_PREVIEW,
  DISMISS_USAGE_WARNING,
  MULTIPLE_ADDON_BUY,
  PAYMENT_METHOD_LIST,
} from "../../../GraphApi/Inventory";
import { AuthContext } from "../../../Context/AuthContext";
import { MyWalletContext } from "../../../Context/MyWalletContext";
import { ToastContext } from "../../../Context/ToastContext";
import { getPagePermissions } from "../../../Utils/RolesAndPermission";
import {
  getNumberByUserFormat,
  getSettings,
} from "../../../Utils/CurrentSetting";
import "./global-usage-warning-modal.css";

const REQUIRED_LIMIT_ADDON_TYPES = new Set([
  "database_storage",
  "processing_units",
  "space",
]);

const formatWarningQuantity = (settings, warning) => {
  const value = Number(warning || 0);
  return Number.isFinite(value) ? getNumberByUserFormat(settings, value) : "0";
};

const getWarningSortRank = (warning) => {
  const threshold = Number(warning?.threshold || 0);
  const limitClass = warning?.limitClass || "soft";

  if (limitClass === "enforced" && threshold >= 100) {
    return 4;
  }

  if (limitClass === "soft" && threshold >= 100) {
    return 3;
  }

  if (limitClass === "enforced") {
    return 2;
  }

  return 1;
};

const getAddonType = (warning) => {
  const addonType = String(warning?.addonType || warning?.usageKey || "");

  if (
    addonType === "database_storage" ||
    addonType.includes("database_storage") ||
    addonType.includes("db_estimate")
  ) {
    return "database_storage";
  }

  if (
    addonType === "processing_units" ||
    addonType.includes("processing_units") ||
    addonType.includes("automation") ||
    addonType.includes("data_processing")
  ) {
    return "processing_units";
  }

  return addonType;
};

const isRequiredLimitAddonType = (warning) =>
  REQUIRED_LIMIT_ADDON_TYPES.has(getAddonType(warning));

const getSuggestedLimitMultiplier = (warning) => {
  const addonType = getAddonType(warning);

  if (addonType === "database_storage") {
    return 1.4;
  }

  if (addonType === "processing_units") {
    return 1.2;
  }

  return 1.2;
};

const getSuggestedLimit = (warning) => {
  const usedQuantity = Number(warning?.usedQuantity || 0);
  const includedQuantity = Number(warning?.includedQuantity || 0);
  const addonType = getAddonType(warning);
  const multiplier = getSuggestedLimitMultiplier(warning);

  if (
    addonType === "processing_units" &&
    usedQuantity <= 0 &&
    includedQuantity <= 0
  ) {
    return 1;
  }

  const suggestedLimit = Math.max(includedQuantity, usedQuantity * multiplier);

  if (!Number.isFinite(suggestedLimit)) {
    return includedQuantity;
  }

  return Math.ceil(suggestedLimit);
};

const getSuggestedAddonState = (warnings) => {
  return warnings.reduce((recommendedAddons, warning) => {
    const addonType = getAddonType(warning);

    if (!warning?.addonAvailable || !addonType) {
      return recommendedAddons;
    }

    const includedQuantity = Number(warning?.includedQuantity || 0);
    const suggestedLimit = getSuggestedLimit(warning);
    const additionalQuantity = Math.max(
      0,
      Math.ceil(suggestedLimit - includedQuantity)
    );

    if (additionalQuantity > 0) {
      recommendedAddons[addonType] = Math.max(
        recommendedAddons[addonType] || 0,
        additionalQuantity
      );
    }

    return recommendedAddons;
  }, {});
};

const formatAddonQuantity = (category, count) => {
  if (count === -1) {
    return "Unlimited";
  }

  if (category === "database_storage") {
    return `${count} GB`;
  }

  if (category === "processing_units") {
    return `${count} Units`;
  }

  return count;
};

const formatUsageQuantity = (settings, warning, quantity) => {
  const addonType = getAddonType(warning);
  const formattedQuantity = formatWarningQuantity(settings, quantity);

  if (addonType === "database_storage") {
    return `${formattedQuantity} GB`;
  }

  if (addonType === "processing_units") {
    return `${formattedQuantity} Units`;
  }

  return formattedQuantity;
};

const getDisplayedSuggestedLimit = (warning, selectedOption) => {
  const selectedCount = Number(selectedOption?.count);

  if (Number.isFinite(selectedCount) && selectedCount > 0) {
    return Number(warning?.includedQuantity || 0) + selectedCount;
  }

  return Number(warning?.includedQuantity || 0);
};

const getRecommendedAddonOption = (warning, options) => {
  const includedQuantity = Number(warning?.includedQuantity || 0);
  const suggestedLimit = getSuggestedLimit(warning);
  const requiredQuantity = Math.max(
    0,
    Math.ceil(suggestedLimit - includedQuantity)
  );

  if (!Array.isArray(options) || options.length === 0 || requiredQuantity <= 0) {
    return null;
  }

  return (
    options.find((option) => Number(option.count || 0) >= requiredQuantity) ||
    options[options.length - 1]
  );
};

const getSuggestedLimitDisplayQuantity = (warning, selectedOption, options) => {
  if (selectedOption) {
    return getDisplayedSuggestedLimit(warning, selectedOption);
  }

  if (!Array.isArray(options) || options.length === 0) {
    return getSuggestedLimit(warning);
  }

  return Number(warning?.includedQuantity || 0);
};

const GlobalUsageWarningModal = () => {
  const { userData } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const walletContext = useContext(MyWalletContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissedWarningIds, setDismissedWarningIds] = useState([]);
  const [availableAddons, setAvailableAddons] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [sliderTouchedAddons, setSliderTouchedAddons] = useState({});
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getUsagePreview, { data }] = useLazyQuery(CUSTOMER_USAGE_PREVIEW, {
    fetchPolicy: "no-cache",
  });
  const [getAddons] = useLazyQuery(ADDON_LIST_IND, {
    fetchPolicy: "network-only",
  });
  const [getPaymentMethods] = useLazyQuery(PAYMENT_METHOD_LIST, {
    fetchPolicy: "network-only",
  });
  const [dismissUsageWarning] = useMutation(DISMISS_USAGE_WARNING);
  const [buyMultipleAddon] = useMutation(MULTIPLE_ADDON_BUY);

  const currentTeam = userData?.authData?.currentTeam || null;
  const currentTeamSettings = currentTeam?.settings || [];
  const currentCompanySetting = useMemo(
    () =>
      Array.isArray(currentTeamSettings) && currentTeamSettings.length > 0
        ? getSettings(currentTeamSettings)
        : null,
    [currentTeamSettings]
  );
  const authenticated = Boolean(userData?.authenticated);
  const teamId = currentTeam?.id ? parseInt(currentTeam.id, 10) : null;
  const billingPermissions = useMemo(
    () => getPagePermissions(userData, "account_billing"),
    [userData]
  );
  const hasBillingAccess = Boolean(billingPermissions?.view?.isAllow);
  const usageWarnings = data?.customerUsagePreview?.usage_warnings || [];
  const visibleUsageWarnings = useMemo(
    () =>
      usageWarnings.filter(
        (warning) => !dismissedWarningIds.includes(String(warning.eventId))
      ),
    [usageWarnings, dismissedWarningIds]
  );
  const modalUsageWarnings = useMemo(() => {
    return [...visibleUsageWarnings].sort((left, right) => {
      const leftRank = getWarningSortRank(left);
      const rightRank = getWarningSortRank(right);

      if (leftRank !== rightRank) {
        return rightRank - leftRank;
      }

      const thresholdDiff =
        Number(right?.threshold || 0) - Number(left?.threshold || 0);
      if (thresholdDiff !== 0) {
        return thresholdDiff;
      }

      return Number(right?.percentUsed || 0) - Number(left?.percentUsed || 0);
    });
  }, [visibleUsageWarnings]);
  const hasBlockingModalWarning = modalUsageWarnings.some(
    (warning) => warning.dismissible === false
  );
  const addonWarnings = useMemo(
    () => modalUsageWarnings.filter((warning) => warning.addonAvailable),
    [modalUsageWarnings]
  );
  const requiredAddonCategories = useMemo(
    () => [
      ...new Set(
        addonWarnings
          .filter((warning) => isRequiredLimitAddonType(warning))
          .map((warning) => getAddonType(warning))
      ),
    ],
    [addonWarnings]
  );
  const selectedAddonsTotal = useMemo(() => {
    return Object.values(selectedAddons).reduce((total, option) => {
      const price = Number(option?.price || 0);
      return total + (Number.isFinite(price) ? price : 0);
    }, 0);
  }, [selectedAddons]);
  const allRequiredAddonsSelected = useMemo(() => {
    if (requiredAddonCategories.length === 0) {
      return true;
    }

    return requiredAddonCategories.every((category) => selectedAddons[category]);
  }, [requiredAddonCategories, selectedAddons]);
  const isAccountBillingRoute = location.pathname.startsWith(
    "/settings/accounts-and-billing"
  );

  useEffect(() => {
    setDismissedWarningIds([]);
    setAvailableAddons({});
    setSelectedAddons({});
    setSliderTouchedAddons({});
    setConsent(false);
  }, [teamId]);

  useEffect(() => {
    if (!authenticated || !teamId || !hasBillingAccess) {
      return;
    }

    if (isAccountBillingRoute) {
      return;
    }

    getUsagePreview({
      variables: {
        team_id: teamId,
      },
      fetchPolicy: "no-cache",
    }).catch(() => null);
  }, [
    authenticated,
    getUsagePreview,
    hasBillingAccess,
    isAccountBillingRoute,
    location.pathname,
    teamId,
  ]);

  useEffect(() => {
    if (!authenticated || !teamId || isAccountBillingRoute || addonWarnings.length === 0) {
      return;
    }

    getAddons({
      variables: {
        team_id: teamId,
        systemPlanId: null,
      },
      fetchPolicy: "network-only",
    })
      .then((result) => {
        const addonData = result?.data?.getAddons?.data;
        if (!addonData) {
          return;
        }

        setAvailableAddons(JSON.parse(addonData));
      })
      .catch(() => null);
  }, [
    addonWarnings.length,
    authenticated,
    getAddons,
    isAccountBillingRoute,
    teamId,
  ]);

  useEffect(() => {
    if (addonWarnings.length === 0 || Object.keys(availableAddons).length === 0) {
      return;
    }

    setSelectedAddons((current) => {
      const nextSelectedAddons = { ...current };
      let hasChanged = false;

      addonWarnings.forEach((warning) => {
        const category = getAddonType(warning);

        if (!category || nextSelectedAddons[category] || sliderTouchedAddons[category]) {
          return;
        }

        const recommendedOption = getRecommendedAddonOption(
          warning,
          availableAddons[category] || []
        );

        if (recommendedOption) {
          nextSelectedAddons[category] = recommendedOption;
          hasChanged = true;
        }
      });

      return hasChanged ? nextSelectedAddons : current;
    });
  }, [addonWarnings, availableAddons, sliderTouchedAddons]);

  const handleUsageWarningDismiss = () => {
    const dismissibleWarnings = modalUsageWarnings.filter(
      (warning) => warning.dismissible !== false
    );

    if (dismissibleWarnings.length === 0 || !teamId) {
      return;
    }

    Promise.all(
      dismissibleWarnings.map((warning) =>
        dismissUsageWarning({
          variables: {
            eventId: warning.eventId,
            team_id: teamId,
          },
        })
      )
    )
      .then(() => {
        setDismissedWarningIds((current) => [
          ...new Set([
            ...current,
            ...dismissibleWarnings.map((warning) => String(warning.eventId)),
          ]),
        ]);
        getUsagePreview({
          variables: {
            team_id: teamId,
          },
          fetchPolicy: "no-cache",
        }).catch(() => null);
      })
      .catch(() => null);
  };

  const handleUsageWarningAddonClick = () => {
    navigate("/settings/accounts-and-billing", {
      state: {
        redirectPageParams: {
          activeTab: "addons",
          openBuyScreen: true,
          recommendedAddons: getSuggestedAddonState(modalUsageWarnings),
        },
      },
    });
  };

  const handleSliderChange = (category, sliderValue) => {
    const options = availableAddons[category] || [];

    if (!options.length || sliderValue > options.length) {
      return;
    }

    setSliderTouchedAddons((current) => ({
      ...current,
      [category]: true,
    }));

    setSelectedAddons((current) => {
      const nextSelectedAddons = { ...current };

      if (sliderValue === 0) {
        delete nextSelectedAddons[category];
        return nextSelectedAddons;
      }

      nextSelectedAddons[category] = options[sliderValue - 1];
      return nextSelectedAddons;
    });
  };

  const handleUpdateAccountLimits = () => {
    const selectedAddonIds = Object.values(selectedAddons)
      .map((addon) => parseInt(addon?.id, 10))
      .filter((id) => Number.isFinite(id));

    if (selectedAddonIds.length === 0) {
      addToast(
        {
          id: "usage-warning-addon-required",
          message: "Select at least one add-on limit to update.",
          title: "Add-on required",
        },
        "danger",
        5000
      );
      return;
    }

    if (!consent) {
      addToast(
        {
          id: "usage-warning-addon-consent-required",
          message:
            "Please confirm the add-on charge before updating account limits.",
          title: "Consent required",
        },
        "danger",
        5000
      );
      return;
    }

    setIsSubmitting(true);
    getPaymentMethods({
      variables: {
        team_id: teamId,
      },
      fetchPolicy: "network-only",
    })
      .then((paymentResult) => {
        const paymentMethods = paymentResult?.data?.getPaymentMethods || [];

        if (paymentMethods.length === 0) {
          handleUsageWarningAddonClick();
          return null;
        }

        return buyMultipleAddon({
          variables: {
            id: selectedAddonIds,
            team_id: teamId,
          },
        });
      })
      .then((purchaseResult) => {
        if (!purchaseResult) {
          return;
        }

        walletContext?.fetchWallet?.();
        addToast(
          {
            id: "usage-warning-addon-success",
            message: "Your account limits have been updated.",
            title: "Limits updated",
          },
          "success",
          5000
        );
        getUsagePreview({
          variables: {
            team_id: teamId,
          },
          fetchPolicy: "no-cache",
        }).catch(() => null);
      })
      .catch((error) => {
        addToast(
          {
            id: "usage-warning-addon-error",
            message: error?.message || "Unable to update account limits.",
            title: "Update failed",
          },
          "danger",
          5000
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (
    !authenticated ||
    !hasBillingAccess ||
    isAccountBillingRoute ||
    modalUsageWarnings.length === 0
  ) {
    return null;
  }

  return (
    <Modal
      show={modalUsageWarnings.length > 0}
      onHide={hasBlockingModalWarning ? undefined : handleUsageWarningDismiss}
      centered
      backdrop={hasBlockingModalWarning ? "static" : true}
      keyboard={!hasBlockingModalWarning}
      className="common modalLg global-usage-warning-modal"
    >
      <Modal.Header
        closeButton={!hasBlockingModalWarning}
        className="modal-head"
      >
        <div className="global-usage-warning-modal-title-wrap">
          <span className="global-usage-warning-modal-title-icon">
            <i className="ri-alert-line"></i>
          </span>
          <div>
            <Modal.Title>
              {hasBlockingModalWarning
                ? "Usage limit reached"
                : "Usage limit update"}
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="global-usage-warning-modal-intro">
          <div>
            <p className="global-usage-warning-modal-copy">
              In order to make your account work smoothly, Update your "Usage
              Limits".
            </p>
            <p className="global-usage-warning-modal-copy global-usage-warning-modal-copy-muted">
              Based on your monthly usage, You need to update the following
              limits.
            </p>
          </div>
        </div>
        <div className="global-usage-warning-modal-list">
          {modalUsageWarnings.map((warning) => {
            const category = getAddonType(warning);
            const options = availableAddons[category] || [];
            const selectedOption = selectedAddons[category] || null;
            const sliderValue = selectedOption
              ? options.findIndex((option) => option.id === selectedOption.id) + 1
              : 0;
            const sliderProgress = options.length
              ? Math.round((sliderValue / options.length) * 100)
              : 0;

            return (
            <div
              key={warning.eventId}
              className={`global-usage-warning-modal-item ${
                Number(warning.threshold || 0) >= 100 ? "is-critical" : "is-warning"
              }`}
            >
              <div className="global-usage-warning-modal-item-head">
                <strong>{warning.label}</strong>
                <span>Resource limit</span>
              </div>
              <div className="global-usage-warning-modal-limit-row">
                <div>
                  <span>Account Limit</span>
                  <strong>
                    {formatUsageQuantity(
                      currentCompanySetting,
                      warning,
                      warning.includedQuantity
                    )}
                  </strong>
                </div>
                <div>
                  <span>Consumed</span>
                  <strong>
                    {formatUsageQuantity(
                      currentCompanySetting,
                      warning,
                      warning.usedQuantity
                    )}
                  </strong>
                </div>
                <div>
                  <span>Suggested Limit</span>
                  <strong>
                    {formatUsageQuantity(
                      currentCompanySetting,
                      warning,
                      getSuggestedLimitDisplayQuantity(
                        warning,
                        selectedOption,
                        options
                      )
                    )}
                  </strong>
                </div>
                <div>
                  <span>Cost</span>
                  <strong>
                    {selectedOption?.price
                      ? `$${Number(selectedOption.price).toFixed(2)}`
                      : "$0"}
                  </strong>
                </div>
              </div>
              {options.length > 0 ? (
                <div className="global-usage-warning-modal-slider">
                  <Form.Range
                    min={0}
                    max={options.length}
                    step={1}
                    value={sliderValue}
                    onChange={(event) =>
                      handleSliderChange(category, parseInt(event.target.value, 10))
                    }
                    style={{
                      "--usage-slider-progress": `${sliderProgress}%`,
                    }}
                  />
                  <span>
                    Add-on selected:{" "}
                    {!selectedOption
                      ? "None"
                      : formatAddonQuantity(
                          category,
                          selectedOption.count
                        )}
                  </span>
                </div>
              ) : null}
            </div>
            );
          })}
        </div>
        <div className="global-usage-warning-modal-total-row">
          <strong>Total Cost</strong>
          <span>
            {selectedAddonsTotal > 0
              ? `$${selectedAddonsTotal.toFixed(2)} per month`
              : "Select add-on limits"}
          </span>
        </div>
        <Form.Check
          className="global-usage-warning-modal-consent"
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          label={`I consent to updating my account limits with the selected add-ons${
            selectedAddonsTotal > 0
              ? ` for $${selectedAddonsTotal.toFixed(2)} per month`
              : ""
          }.`}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={
            addonWarnings.length > 0
              ? handleUpdateAccountLimits
              : handleUsageWarningAddonClick
          }
          disabled={
            isSubmitting ||
            (addonWarnings.length > 0 &&
              (!allRequiredAddonsSelected || selectedAddonsTotal <= 0 || !consent))
          }
        >
          {isSubmitting ? "Updating..." : "Update Account Limits"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GlobalUsageWarningModal;
